import { advanceByWorkingDays, computeCPM, retreatByWorkingDays, taskAllowsWeekend, taskShiftsPerDay } from "./cpm";
import type { MonteCarloResult, Task } from "./types";

/** Triangular-distribution sampler — same as the original implementation. */
export function sampleTriangular(o: number, m: number, p: number): number {
  if (p <= o) return m;
  const mm = Math.min(Math.max(m, o), p);
  const u = Math.random();
  const fc = (mm - o) / (p - o);
  if (u < fc) return o + Math.sqrt(u * (p - o) * (mm - o));
  return p - Math.sqrt((1 - u) * (p - o) * (p - mm));
}

function percentile(sorted: number[], p: number): number {
  if (!sorted.length) return 0;
  const idx = (sorted.length - 1) * p;
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return sorted[lo];
  const frac = idx - lo;
  return sorted[lo] + (sorted[hi] - sorted[lo]) * frac;
}

/**
 * Runs `iterations` triangular-sampled passes through the network and returns the
 * project-duration distribution. This is what correctly captures "merge bias" — a
 * simple sum-of-critical-path PERT calculation on the deterministic path cannot.
 */
export function runMonteCarlo(
  tasks: Task[],
  iterations: number,
  allowWeekendWork: boolean,
  projectShiftsPerDay: 1 | 2 = 1
): MonteCarloResult {
  const n = Math.max(1, Math.floor(iterations) || 1);
  const samples: number[] = [];

  // Topological order (and the successor map the backward pass needs) is fixed by the
  // dependency graph regardless of sampled durations, so it's built once rather than
  // per iteration.
  const topo = topoOrder(tasks);
  const criticalCount: Record<string, number> = {};
  tasks.forEach((t) => { criticalCount[t.id] = 0; });

  for (let iter = 0; iter < n; iter++) {
    if (topo === null) { samples.push(NaN); continue; }
    const { order, succ } = topo;

    const sampled = tasks.map((t) => ({
      ...t,
      _dur: t.type === "milestone" ? 0 : sampleTriangular(t.o, t.m, t.p) / taskShiftsPerDay(t, projectShiftsPerDay),
    }));
    const idx: Record<string, (typeof sampled)[number]> = {};
    sampled.forEach((t) => { idx[t.id] = t; });

    const ES: Record<string, number> = {};
    const EF: Record<string, number> = {};
    order.forEach((id) => {
      const t = idx[id];
      const own = taskAllowsWeekend(t, allowWeekendWork);
      let es = 0;
      t.preds.forEach((p: string) => { es = Math.max(es, EF[p] ?? 0); });
      if (t.pinnedDay != null && !isNaN(t.pinnedDay)) es = Math.max(es, t.pinnedDay);
      ES[id] = es;
      EF[id] = advanceByWorkingDays(es, t._dur, own);
    });
    let projectEnd = 0;
    order.forEach((id) => { projectEnd = Math.max(projectEnd, EF[id]); });
    samples.push(projectEnd);

    // Backward pass over this iteration's sampled durations — tallies how often each
    // task comes out with zero slack, i.e. how often normal duration uncertainty alone
    // (not just the deterministic M-duration schedule) is enough to put it on the
    // critical path. This is what a per-task "criticality index" is built from.
    const LS: Record<string, number> = {};
    for (let k = order.length - 1; k >= 0; k--) {
      const id = order[k];
      const own = taskAllowsWeekend(idx[id], allowWeekendWork);
      const s = succ[id];
      const lf = s.length ? Math.min(...s.map((v) => LS[v])) : projectEnd;
      LS[id] = retreatByWorkingDays(lf, idx[id]._dur, own);
      if (Math.abs(LS[id] - ES[id]) < 1e-6) criticalCount[id]++;
    }
  }

  const sorted = samples.filter((s) => !isNaN(s)).sort((a, b) => a - b);
  const mean = sorted.length ? sorted.reduce((a, b) => a + b, 0) / sorted.length : 0;
  const criticalityIndex: Record<string, number> = {};
  tasks.forEach((t) => { criticalityIndex[t.id] = n ? criticalCount[t.id] / n : 0; });

  return {
    samples: sorted,
    p10: percentile(sorted, 0.1),
    p50: percentile(sorted, 0.5),
    p90: percentile(sorted, 0.9),
    mean,
    iterations: n,
    criticalityIndex,
  };
}

/** Kahn's-algorithm topological order plus the successor map it builds along the way —
 *  both are duration-independent, so they're computed once and reused across every
 *  sampled iteration (both the forward and the backward pass need them). */
function topoOrder(tasks: Task[]): { order: string[]; succ: Record<string, string[]> } | null {
  const ids = tasks.map((t) => t.id);
  const idSet = new Set(ids);
  const succ: Record<string, string[]> = {};
  const indeg: Record<string, number> = {};
  ids.forEach((i) => { succ[i] = []; indeg[i] = 0; });
  tasks.forEach((t) => {
    t.preds.forEach((p) => {
      if (!idSet.has(p) || p === t.id) return;
      succ[p].push(t.id);
      indeg[t.id]++;
    });
  });
  const queue = ids.filter((i) => indeg[i] === 0);
  const order: string[] = [];
  const indeg2 = { ...indeg };
  while (queue.length) {
    const u = queue.shift()!;
    order.push(u);
    succ[u].forEach((v) => { indeg2[v]--; if (indeg2[v] === 0) queue.push(v); });
  }
  return order.length === ids.length ? { order, succ } : null;
}

// computeCPM is re-exported here so callers only need one import for both engines.
export { computeCPM };
